/**
 * Admin › Finanças › Fundo de Incentivos (W10-INCENTIVE-FUND, D489 + D489b, ADR-0102; doc 17 section 3.1) - the pure view-model behind the balances screen
 * and the owner's investment under four eyes, over `@vello/api-client` `IncentiveFundAdminApi`.
 *
 *  - who may do what (`incentive_fund:view`, `:invest` = the maker, `:approve` = the checker; all GEO - bound to the market the operator picks);
 *  - the envelopes of a market in the fixed side order, the country's COUNTRY envelope apart, exact per-currency totals (bigint, never summed across
 *    currencies) and how much of each envelope is already promised;
 *  - the last 24 hours' and the last 7 days' spend (D839: rolling windows) against the caps the policy publishes (a meter in basis points of the cap, never a float percentage);
 *  - whether THIS operator may decide an investment (never the one who proposed it - the server refuses `FUND_SELF_APPROVAL` anyway, the screen hides the
 *    button and says why), and the draft checks of a proposal before anything is sent;
 *  - the words for each stable refusal.
 * The fund is a MANAGEMENT budget: nothing here is a balance a customer can see. Nothing names a country, a currency or an amount.
 */
import type { IncentiveFundBalancesView, IncentiveFundEnvelopeSide, IncentiveFundEnvelopeView, IncentiveFundInvestmentView, IncentiveFundSpendWindow } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

export type FundTone = 'neutral' | 'success' | 'warning' | 'danger';

export function incentiveFundAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayInvest: boolean; readonly mayApprove: boolean } {
  if (grants === undefined) return { mayView: true, mayInvest: true, mayApprove: true };
  return {
    mayView: grantsAllow(grants, { permission: 'incentive_fund:view', target: 'GEO' }),
    mayInvest: grantsAllow(grants, { permission: 'incentive_fund:invest', target: 'GEO' }),
    mayApprove: grantsAllow(grants, { permission: 'incentive_fund:approve', target: 'GEO' }),
  };
}

/** The sides a market's fund is split into, in the order the screen lists them. */
export const FUND_SIDE_ORDER = ['RIDER', 'DRIVER', 'COURIER', 'MERCHANT_COFUND', 'ACQUISITION'] as const;
export type FundSideName = (typeof FUND_SIDE_ORDER)[number];

/** The stable reasons an owner investment can carry: the mirror of `@vello/incentive-fund` `INVESTMENT_REASON_CODES` (D854 L6c-G05: the server refuses any other code `FUND_REASON_INVALID`). */
export const INVESTMENT_REASONS = ['GROWTH_PUSH', 'MARKET_LAUNCH', 'DRIVER_SUPPLY', 'RIDER_DEMAND', 'MERCHANT_COFUND', 'OTHER'] as const;
export type InvestmentReason = (typeof INVESTMENT_REASONS)[number];

/** How long an investment may stay valid (days). The store refuses more than a year. */
export const INVESTMENT_VALIDITY_DAYS = [30, 60, 90, 180, 365] as const;

export function policyTone(state: IncentiveFundBalancesView['policy']['state']): FundTone {
  return state === 'ACTIVE' ? 'success' : state === 'INVALID' ? 'danger' : state === 'DISABLED' ? 'warning' : 'neutral';
}

export function investmentTone(status: IncentiveFundInvestmentView['status']): FundTone {
  return status === 'APPLIED' ? 'success' : status === 'REJECTED' ? 'danger' : status === 'PROPOSED' ? 'warning' : 'neutral';
}

export interface EnvelopeRow {
  readonly envelopeKey: string;
  readonly side: IncentiveFundEnvelopeSide;
  readonly currency: string;
  readonly creditedMinor: bigint;
  readonly reservedMinor: bigint;
  readonly confirmedMinor: bigint;
  readonly availableMinor: bigint;
  /** Basis points of the credit already reserved or spent (0 for an empty envelope); 0 … 10 000. */
  readonly committedBp: number;
  readonly updatedAt: string;
}

const minor = (v: { readonly amountMinor: string }): bigint => (/^-?\d+$/.test(v.amountMinor) ? BigInt(v.amountMinor) : 0n);

export function envelopeRow(e: IncentiveFundEnvelopeView): EnvelopeRow {
  const credited = minor(e.credited);
  const reserved = minor(e.reserved);
  const confirmed = minor(e.confirmed);
  const committed = reserved + confirmed;
  const bp = credited <= 0n ? 0 : Number((committed * 10_000n) / credited);
  return {
    envelopeKey: e.envelopeKey, side: e.side, currency: e.credited.currency, creditedMinor: credited, reservedMinor: reserved, confirmedMinor: confirmed, availableMinor: minor(e.available),
    committedBp: Math.min(10_000, bp), updatedAt: e.updatedAt,
  };
}

/** The market's envelopes in side order (a missing one is simply absent: an envelope is created by its first credit), and the country's apart. */
export function splitEnvelopes(view: Pick<IncentiveFundBalancesView, 'envelopes'>): { readonly market: readonly EnvelopeRow[]; readonly country: EnvelopeRow | undefined } {
  const rows = view.envelopes.map(envelopeRow);
  const market = rows.filter((r) => r.side !== 'COUNTRY').sort((a, b) => FUND_SIDE_ORDER.indexOf(a.side as FundSideName) - FUND_SIDE_ORDER.indexOf(b.side as FundSideName));
  return { market, country: rows.find((r) => r.side === 'COUNTRY') };
}

/** Exact totals per currency of the market's own envelopes (the COUNTRY envelope is not the market's money). */
export function fundTotals(rows: readonly EnvelopeRow[]): readonly { readonly currency: string; readonly creditedMinor: bigint; readonly reservedMinor: bigint; readonly confirmedMinor: bigint; readonly availableMinor: bigint }[] {
  const by = new Map<string, { credited: bigint; reserved: bigint; confirmed: bigint; available: bigint }>();
  for (const r of rows) {
    if (r.side === 'COUNTRY') continue;
    const t = by.get(r.currency) ?? { credited: 0n, reserved: 0n, confirmed: 0n, available: 0n };
    t.credited += r.creditedMinor;
    t.reserved += r.reservedMinor;
    t.confirmed += r.confirmedMinor;
    t.available += r.availableMinor;
    by.set(r.currency, t);
  }
  return [...by.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, t]) => ({ currency, creditedMinor: t.credited, reservedMinor: t.reserved, confirmedMinor: t.confirmed, availableMinor: t.available }));
}

/** How much of a spend window's cap is used, in basis points (capped at 10 000), and the tone of the meter. A window with no gross has no cap: it reads as fully used. */
export function spendMeter(w: Pick<IncentiveFundSpendWindow, 'cap' | 'spent'>): { readonly usedBp: number; readonly tone: FundTone } {
  const cap = minor(w.cap);
  const spent = minor(w.spent);
  const usedBp = cap <= 0n ? 10_000 : Math.min(10_000, Number((spent * 10_000n) / cap));
  return { usedBp, tone: usedBp >= 10_000 ? 'danger' : usedBp >= 8_000 ? 'warning' : 'success' };
}

/** Why this operator may not approve an investment (or undefined when he may). */
export function investmentApproveBlock(inv: Pick<IncentiveFundInvestmentView, 'proposedBy' | 'status'>, operatorId: string | undefined, mayApprove: boolean, selfApproval = false): 'NOT_PENDING' | 'NO_PERMISSION' | 'OWN_PROPOSAL' | undefined {
  if (inv.status !== 'PROPOSED') return 'NOT_PENDING';
  if (!mayApprove) return 'NO_PERMISSION';
  if (!selfApproval && operatorId !== undefined && inv.proposedBy === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

export type InvestmentDraftIssue = 'SIDE_REQUIRED' | 'AMOUNT_REQUIRED' | 'REASON_REQUIRED' | 'VALIDITY_REQUIRED' | 'NOTE_TOO_LONG';

/** A proposal draft: a side (any envelope, the country's included), a positive amount, a stable reason and a validity from the closed list. */
export function investmentDraftIssue(d: { readonly side: string; readonly amountMinor: bigint | undefined; readonly reasonCode: string; readonly validityDays: number | undefined; readonly note: string }): InvestmentDraftIssue | undefined {
  if (![...FUND_SIDE_ORDER, 'COUNTRY'].includes(d.side)) return 'SIDE_REQUIRED';
  if (d.amountMinor === undefined || d.amountMinor <= 0n) return 'AMOUNT_REQUIRED';
  if (!(INVESTMENT_REASONS as readonly string[]).includes(d.reasonCode)) return 'REASON_REQUIRED';
  if (d.validityDays === undefined || !(INVESTMENT_VALIDITY_DAYS as readonly number[]).includes(d.validityDays)) return 'VALIDITY_REQUIRED';
  if (d.note.length > 500) return 'NOTE_TOO_LONG';
  return undefined;
}

/** The instant an investment proposed `now` stays valid until (UTC ISO). */
export function investmentValidUntil(nowIso: string, validityDays: number): string {
  return new Date(Date.parse(nowIso) + validityDays * 86_400_000).toISOString();
}

